/** Regime tab. Display only — the HMM taxonomy and its calculations are
 * untouched. The four state labels here are exactly the ones the engine emits,
 * plus the "transitioning" gate used when five sessions have no majority.
 */
import { useMemo } from "react";
import { StatusBadge } from "@/components/wheel/StatusBadge";
import {
  CachedBadge, DataNotice, KeyRow, MockBadge, PanelGrid, SectionTitle, StatTile, TileRow, WorkspacePanel,
} from "@/workspace/primitives";
import { REGIME_COLORS, REGIME_SEMANTICS } from "@/workspace/charts";
import { PriceRegimeStack } from "@/workspace/PriceRegimeChart";
import { regimeCompatibility } from "@/workspace/mockTickerData";
import { windowRibbonAvailable } from "@/workspace/live/workspaceLiveAdapter";
import { LIVE_UNAVAILABLE } from "@/workspace/live/workspaceLiveUnavailable";

const VERDICT_BADGE = {
  available: { tier: "info", text: "Historical regime context available" },
  limited: { tier: "info", text: "Historical regime context limited" },
  differs: { tier: "caution", text: "Historical regime context differs" },
};

export function RegimeTab({ t }) {
  const bars = useMemo(() => (t.series || []).slice(-160), [t.series]);
  const ribbonOk = !t.live || windowRibbonAvailable(bars);
  const compat = t.live ? [] : regimeCompatibility(t.regime.state);
  const persistence = t.regime.sojournDays != null && t.regime.sojournP75
    ? Math.min(100, Math.round((t.regime.sojournDays / t.regime.sojournP75) * 100))
    : null;
  const notice = t.live ? <CachedBadge /> : <MockBadge />;

  return (
    <div className="space-y-3">
      <TileRow cols="grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile
          label="Instant regime"
          value={t.regime.instantState}
          tone={REGIME_COLORS[t.regime.instantState]}
          sub={t.regime.confidence != null ? `${Math.round(t.regime.confidence * 100)}% confidence` : "confidence unavailable"}
          testid="regime-tile-instant"
        />
        <StatTile
          label="Confirmed (5 sessions)"
          value={t.regime.confirmedState}
          tone={REGIME_COLORS[t.regime.confirmedState]}
          sub={t.regime.confirmedState === "transitioning" ? "No three-of-five majority" : "Three-of-five majority"}
          testid="regime-tile-confirmed"
        />
        <StatTile label="Sojourn so far" value={t.regime.sojournDays != null ? `${t.regime.sojournDays}d` : "—"} sub={t.regime.sojournP75 != null ? `P75 ${t.regime.sojournP75}d` : "P75 unavailable"} />
        <StatTile label="Regime age" value={t.regime.ageBars != null ? `${t.regime.ageBars} bars` : "—"} />
        <StatTile
          label="Agreement"
          value={t.regime.disagreement ? "Disagree" : "Agree"}
          badge={t.regime.disagreement
            ? <StatusBadge tier="caution" text="Instant differs from confirmed" />
            : <StatusBadge tier="info" text="Instant matches confirmed" />}
          testid="regime-tile-agreement"
        />
      </TileRow>

      <WorkspacePanel
        title="Price and historical regime"
        subtitle="Last 160 sessions · one regime ribbon (Confirmed default) plus the trend tape"
        notice={notice}
        hint={t.live ? "Cached OHLCV with exact-date regime history. Confirmed ribbon is the default; instant is a toggle." : "Fixture bands. Instant history is optional via the ribbon control. This tab does not run the HMM."}
        testid="regime-ribbon-panel"
      >
        {bars.length ? (
          <PriceRegimeStack
            series={bars}
            height={260}
            showVolume
            showSma={!t.live || (t.hasPerBarSma50 || t.hasPerBarSma200)}
            smaKeys={t.live ? t.smaKeys : undefined}
            detailed={!t.live}
            allowRibbonToggle
            ribbonAvailable={ribbonOk}
            alignmentMessage={ribbonOk ? null : (t.alignmentMessage || LIVE_UNAVAILABLE.regimeAlignment)}
            showTrendTape={!t.live || t.hasTrendTape}
            live={Boolean(t.live)}
            testid="regime-price-chart"
          />
        ) : (
          <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>OHLCV unavailable from current cached data.</p>
        )}
      </WorkspacePanel>

      <PanelGrid>
        <WorkspacePanel
          title="Persistence"
          subtitle="Sojourn against the P75 for this state"
          testid="regime-persistence"
        >
          {persistence == null ? (
            <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>Not available in current cached output</p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="h-2 rounded-sm overflow-hidden flex-1" style={{ background: "var(--hoip-hover)" }}>
                  <div style={{ width: `${persistence}%`, height: "100%", background: REGIME_COLORS[t.regime.state] }} />
                </div>
                <span className="text-[11px] font-mono-data shrink-0">{persistence}%</span>
              </div>
              <p className="text-[10px] mt-1.5 leading-snug" style={{ color: "var(--hoip-text-secondary)" }}>
                {persistence >= 100
                  ? "This confirmed state has already run past its typical historical length."
                  : `A tenor beyond ${t.regime.sojournP75} days extends past the typical historical length of this state.`}
              </p>
            </>
          )}
          <div className="mt-2 pt-2 border-t space-y-0.5" style={{ borderColor: "var(--hoip-divider)" }}>
            <KeyRow label="State">{t.regime.state}</KeyRow>
            <KeyRow label="Confidence">{t.regime.confidence != null ? `${Math.round(t.regime.confidence * 100)}%` : "—"}</KeyRow>
            <KeyRow label="P75 sojourn">{t.regime.sojournP75 != null ? `${t.regime.sojournP75}d` : "—"}</KeyRow>
          </div>
        </WorkspacePanel>

        <WorkspacePanel
          title="Instant versus confirmed"
          testid="regime-disagreement"
        >
          {t.regime.disagreement ? (
            <>
              <StatusBadge tier="caution" text="Disagreement" />
              <p className="text-[11px] mt-1.5 leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }}>
                The instant read is <strong>{t.regime.instantState}</strong> while the five-session confirmed state is{" "}
                <strong>{t.regime.confirmedState}</strong>. Historical family context uses the confirmed path; the instant
                read is context only.
              </p>
            </>
          ) : (
            <>
              <StatusBadge tier="info" text="Agreement" />
              <p className="text-[11px] mt-1.5 leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }}>
                Instant and confirmed reads agree on <strong>{t.regime.state}</strong>.
              </p>
            </>
          )}
          <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
            <SectionTitle>What this state means</SectionTitle>
            <p className="text-[10px] leading-relaxed" style={{ color: "var(--hoip-text-secondary)" }}>
              {REGIME_SEMANTICS[t.regime.state] || "Confirmed state has no majority yet, so no state description applies."}
            </p>
          </div>
        </WorkspacePanel>
      </PanelGrid>

      <WorkspacePanel
        title="Historical family context"
        notice={notice}
        hint={t.live ? "No strategy ranking is applied on this tab." : "Illustrative historical mapping. This tab does not rank or instruct a structure."}
        testid="regime-compat"
      >
        {t.live ? (
          <p className="text-[12px]" style={{ color: "var(--hoip-text-secondary)" }}>{LIVE_UNAVAILABLE.familyContext} is listed on Options Income & Hedging when that cached GET is opened.</p>
        ) : null}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          {compat.map((c) => {
            const v = VERDICT_BADGE[c.verdict];
            return (
              <div
                key={c.family}
                className="flex items-start justify-between gap-2 py-1.5 border-t"
                style={{ borderColor: "var(--hoip-divider)" }}
              >
                <div className="min-w-0">
                  <div className="text-[11px]">{c.family}</div>
                  <div className="text-[9px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}>{c.note}</div>
                </div>
                <span className="shrink-0"><StatusBadge tier={v.tier} text={v.text} /></span>
              </div>
            );
          })}
        </div>
        <div className="mt-2 pt-2 border-t" style={{ borderColor: "var(--hoip-divider)" }}>
          <DataNotice adminText="HMM taxonomy, transition matrix, sojourn statistics and the confirmed-state gate are unchanged. This tab renders them and computes nothing." />
        </div>
      </WorkspacePanel>
    </div>
  );
}
